import { useEffect, useState } from 'react';
import type { Heading } from '../types/doc';

interface Props {
  headings: Heading[];
}

export default function DocTOC({ headings }: Props) {
  const visible = headings.filter((h) => h.level >= 2 && h.level <= 3);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (visible.length === 0) return;
    const elements = visible
      .map((h) => document.getElementById(h.id))
      .filter((el): el is HTMLElement => !!el);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry whose top is closest to the viewport top among
        // currently intersecting headings.
        const intersecting = entries.filter((e) => e.isIntersecting);
        if (intersecting.length === 0) return;
        intersecting.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActiveId(intersecting[0].target.id);
      },
      { rootMargin: '-80px 0px -65% 0px', threshold: [0, 1] },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [visible]);

  if (visible.length < 3) return <aside className="doc-toc doc-toc--empty" aria-hidden="true" />;

  return (
    <aside className="doc-toc" aria-label="On this page">
      <p className="doc-toc__label">On this page</p>
      <ol className="doc-toc__list">
        {visible.map((h) => (
          <li
            key={h.id}
            className={`doc-toc__item doc-toc__item--l${h.level}${activeId === h.id ? ' doc-toc__item--active' : ''}`}
          >
            <a href={`#${h.id}`}>{h.text}</a>
          </li>
        ))}
      </ol>
    </aside>
  );
}
